import type { ContractsValueObjectsTimeConditionEnum } from 'generated/resources/graphql'
import { Moment } from 'moment'

import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { Model } from 'modules/core/lib/mobx-rest'
import MoneyWithCurrency from 'modules/core/lib/money'

import type Employee from 'modules/employees/store/models/Employee'

/**
 * The basic type of units as opposed to a valid currency string.
 */
export const UNITS_TYPE_UNITS = 'units'
export const UNITS_TYPE_MONEY = 'money'

export const CUSTOM_TIME_CONDITION: ContractsValueObjectsTimeConditionEnum = 'custom'

interface ICompensation {
  [key: string]: any
}

interface GetUnitValueOptions {
  compensation: Compensation | undefined
  employee: Employee | undefined
  type?: string | undefined
  locale: string
}
const yearlyEquivalent = 'yearly_equivalent'

class Compensation extends Model<ICompensation> {
  static recurrences: () => { [key: string]: string } = () => ({
    monthly: i18n.t('common.monthly').toLowerCase(),
    every_2_months: i18n.t('common.every_2_months').toLowerCase(),
    every_3_months: i18n.t('common.every_3_months').toLowerCase(),
    every_4_months: i18n.t('common.every_4_months').toLowerCase(),
    every_5_months: i18n.t('common.every_5_months').toLowerCase(),
    every_6_months: i18n.t('common.every_6_months').toLowerCase(),
    every_7_months: i18n.t('common.every_7_months').toLowerCase(),
    every_8_months: i18n.t('common.every_8_months').toLowerCase(),
    every_9_months: i18n.t('common.every_9_months').toLowerCase(),
    every_10_months: i18n.t('common.every_10_months').toLowerCase(),
    every_11_months: i18n.t('common.every_11_months').toLowerCase(),
    every_12_months: i18n.t('common.every_12_months').toLowerCase(),
  })

  static Types: () => { [key: string]: string } = () => ({
    undefined: i18n.t('compensation.types.undefined'),
    fixed: i18n.t('compensation.types.fixed'),
    per_worked_day: i18n.t('compensation.types.per_worked_day'),
    up_to: i18n.t('compensation.types.up_to'),
  })

  static Unit: (employee: Employee | undefined) => { [key: string]: string } = (
    employee: Employee | undefined
  ) =>
    ({
      money: String(employee?.attributes.legal_entity_currency),
      units: i18n.t('compensation.unit.base'),
    } as const)

  static ItemTypes: () => { [key: string]: string } = () => ({
    undefined: i18n.t('compensation.types.undefined'),
    fixed: i18n.t('compensation.types.fixed'),
    up_to: i18n.t('compensation.types.up_to'),
    per_worked_day: i18n.t('compensation.types.per_worked_day'),
  })

  static CalculationTypes: () => { [key: string]: string } = () => ({
    current_period: i18n.t('compensation.calculation_types.current_period'),
    previous_period: i18n.t('compensation.calculation_types.previous_period'),
  })

  static TimeConditionTypes: () => Record<ContractsValueObjectsTimeConditionEnum, string> = () => ({
    full_day: i18n.t(
      'contract_versions.forms.additional_compensation.modal.time_condition_selector.full_day'
    ),
    half_day: i18n.t(
      'contract_versions.forms.additional_compensation.modal.time_condition_selector.half_day'
    ),
    custom: i18n.t(
      'contract_versions.forms.additional_compensation.modal.time_condition_selector.custom'
    ),
  })

  static calculations = {
    current_period: 'current_period',
    previous_period: 'previous_period',
  }

  static urlRoot() {
    return '/contracts/compensations'
  }

  static types = {
    undefined: 'undefined',
    fixed: 'fixed',
    up_to: 'up_to',
    per_worked_day: 'per_worked_day',
    per_worked_hour: 'per_worked_hour',
  }

  static syncableTypes = [
    Compensation.types.undefined,
    Compensation.types.fixed,
    Compensation.types.up_to,
    Compensation.types.per_worked_day,
  ]

  static GetUnitValue = ({ compensation, employee, type, locale }: GetUnitValueOptions) => {
    const CENTS = 100
    const yearlyEquivalentAmount = compensation?.attributes.yearly_equivalent_amount

    if (type == yearlyEquivalent && yearlyEquivalentAmount == null) return null

    const compensationAmount = compensation?.attributes.amount || 0
    const amount = type == yearlyEquivalent ? yearlyEquivalentAmount : compensationAmount
    const isTypeUnits = compensation?.attributes.unit === UNITS_TYPE_UNITS

    if (isTypeUnits) return i18n.tp('compensation.unit.units', { count: Number(amount) / CENTS })

    return MoneyWithCurrency({
      amount: Number(amount),
      currency: employee?.attributes.legal_entity_currency,
      optionalDecimals: true,
      locale,
    })
  }

  static yearlyEquivalentAmount = (
    compensation: Compensation | undefined,
    employee: Employee | undefined,
    locale: string
  ) => {
    switch (compensation?.attributes.compensation_type) {
      case 'undefined':
        return ''
      default:
        return this.GetUnitValue({ compensation, employee, type: yearlyEquivalent, locale })
    }
  }

  static amount = (
    compensation: Compensation | undefined,
    employee: Employee | undefined,
    locale: string
  ) => {
    switch (compensation?.attributes.compensation_type) {
      case 'undefined':
        return ''
      default:
        return this.GetUnitValue({ compensation, employee, locale })
    }
  }

  isAmountReadOnly(): boolean {
    const type = this.attributes.compensation_type

    return [
      Compensation.types.fixed,
      Compensation.types.per_worked_day,
      Compensation.types.per_worked_hour,
    ].includes(type)
  }

  get firstPaymentOn(): Moment | null {
    return this.attributes.first_payment_on
      ? stringToDate(this.attributes.first_payment_on, 'backend')
      : null
  }

  get startsOn(): Moment | null {
    return this.attributes.starts_on ? stringToDate(this.attributes.starts_on, 'backend') : null
  }
}

export default Compensation
